import carSheet from '../../assets/map/white-car-360-runtime.png';
import amberCarSheet from '../../assets/map/amber-car-360-runtime.png';
import redCarSheet from '../../assets/map/red-car-360-runtime.png';

/**
 * Picks the car image for a heading.
 *
 * Two ways to draw a car that faces where it is going: rotate one top-down
 * image, or render the vehicle from every angle and show the matching frame.
 * The second is what makes it read as three-dimensional — body, windows and
 * shadow all change with the angle, instead of a flat shape spinning.
 *
 * The sheet is the white fleet car rendered in Blender from one fixed
 * orthographic camera ~60° above the ground: 2560x2304, 20 columns x 18 rows
 * of 128px cells, one frame per whole degree, row-major from 0 to 359.
 * Bearing 0 faces north (the car's rear), increasing clockwise, each cell
 * anchored at its centre. `white-car-360-runtime.json` ships beside the PNG as
 * the generator's own index; `carSprite.test.js` checks this arithmetic
 * against every one of its 360 entries.
 */
export const SPRITE = {
  url: carSheet,
  /** One frame per degree — no rounding to a coarser step. */
  frameCount: 360,
  /** Source pixels per frame (square). */
  framePx: 128,
  /** Frames per row in the sheet. */
  columns: 20,
  /** Drawn size on the map. 128px cells cover this at 2x pixel density. */
  displayPx: 56,
};

/**
 * The same car in amber and red, for flagging a late driver: identical sheet
 * layout (2560x2304, 20x18 frames of 128px, one per degree, same camera), so
 * every frame lines up with the white one and only the paint changes.
 */
export const AMBER_SPRITE = { ...SPRITE, url: amberCarSheet };
export const RED_SPRITE = { ...SPRITE, url: redCarSheet };

/**
 * Minutes late a driver can be before their car is flagged — for the first
 * pick-up of the trip they're driving, or the pick-up of the return after it.
 * Over `atRisk` is amber, over `late` red. These are the defaults; each admin
 * can set their own (see useLatenessThresholds).
 */
export const DEFAULT_LATENESS_THRESHOLDS = { atRisk: 15, late: 30 };

/** 'late' | 'at_risk' | null (on time, or no forecast) for minutes late. */
export const latenessTier = (minutes, thresholds = DEFAULT_LATENESS_THRESHOLDS) => {
  if (!Number.isFinite(minutes) || minutes <= thresholds.atRisk) return null;
  return minutes > thresholds.late ? 'late' : 'at_risk';
};

/**
 * A live driver's worse forecast: late to their first pick-up, or late for
 * their return. Null when there's neither.
 */
export const driverLateMinutes = (driver) => {
  const forecasts = [driver?.pickupLateMinutes, driver?.returnLateMinutes].filter(Number.isFinite);
  return forecasts.length ? Math.max(...forecasts) : null;
};

/** The car to draw for a driver forecast `minutes` late. */
export const spriteForLateness = (minutes, thresholds = DEFAULT_LATENESS_THRESHOLDS) =>
  ({ late: RED_SPRITE, at_risk: AMBER_SPRITE })[latenessTier(minutes, thresholds)] || SPRITE;

/**
 * Whether the Maps script has progressed far enough to use its geometry types.
 *
 * `window.google` appears before `google.maps` is populated, so checking the
 * namespace is not enough — the constructors have to exist. Anything touching
 * them during render has to ask, because JSX children are evaluated eagerly,
 * before the map itself decides whether to mount them.
 */
export const mapsReady = () =>
  typeof window !== 'undefined' &&
  typeof window.google?.maps?.Size === 'function' &&
  typeof window.google?.maps?.Point === 'function';

/**
 * Degrees between the frames actually used. The sheet holds all 360, but
 * showing a different render every degree makes the car shimmer: a tweened
 * heading crosses several degrees a second, and a real device's bearing
 * dithers a few degrees while driving dead straight. At the size a car draws
 * on a map, 5° apart is indistinguishable per frame and much calmer in motion.
 */
export const BEARING_STEP_DEGREES = 5;

/**
 * The heading to actually draw, given what is currently drawn.
 *
 * Quantises to {@link BEARING_STEP_DEGREES}, and holds the current frame until
 * the heading has moved a whole step away from it. Without that deadband a
 * bearing hovering on a boundary flips between two renders every frame, which
 * is the worst-looking case of all.
 *
 * @param  {number|null} previous - The heading currently drawn, or null
 * @param  {number} next - The heading now reported
 * @param  {number} step - Degrees between usable frames
 * @returns {number} - The heading to draw
 */
export const stableBearing = (previous, next, step = BEARING_STEP_DEGREES) => {
  const normalized = (((next ?? 0) % 360) + 360) % 360;
  const snapped = (Math.round(normalized / step) * step) % 360;
  if (previous === null || previous === undefined) {
    return snapped;
  }
  const delta = Math.abs(((((normalized - previous) % 360) + 540) % 360) - 180);
  return delta < step ? previous : snapped;
};

/**
 * The frame whose angle is closest to `bearing`, wrapping 360° back to 0.
 *
 * @param  {number} bearing - Heading in degrees, any sign or magnitude
 * @param  {number} frameCount - Frames in the sheet
 * @returns {number} - Frame index, 0-based
 */
export const frameForBearing = (bearing, frameCount = SPRITE.frameCount) => {
  const normalized = (((bearing ?? 0) % 360) + 360) % 360;
  return Math.round(normalized / (360 / frameCount)) % frameCount;
};

/**
 * The CSS for one frame of the sheet, as a background crop.
 *
 * A background-position crop rather than a `google.maps.Icon`, because the car
 * is drawn in an OverlayView rather than a Marker: Marker rounds its position
 * to whole pixels, and at a zoom that frames a whole trip a location ping moves
 * the car less than a pixel — so it sat still for several pings and then
 * hopped. An overlay is positioned from `fromLatLngToDivPixel`, which is
 * fractional, so the same motion renders as a smooth creep.
 *
 * Google scales a sprite by the WHOLE sheet, and so does CSS: `backgroundSize`
 * is the sheet at display scale and the offset indexes that scaled sheet, not
 * the source image. Getting that wrong shows a slice of four neighbouring
 * frames.
 */
export const spriteStyle = (bearing, sprite = SPRITE) => {
  const { frameCount, columns, displayPx, url } = sprite;
  const rows = Math.ceil(frameCount / columns);
  const frame = frameForBearing(bearing, frameCount);
  const column = frame % columns;
  const row = Math.floor(frame / columns);

  return {
    width: displayPx,
    height: displayPx,
    backgroundImage: `url(${url})`,
    backgroundSize: `${columns * displayPx}px ${rows * displayPx}px`,
    backgroundPosition: `-${column * displayPx}px -${row * displayPx}px`,
  };
};
